import { useState } from 'react';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import BodyMap from './BodyMap';
import { getMuscleLabel } from './exercises';
import { IconShare } from './icons';
import {
  muscleBarData,
  muscleSetsInRange,
  muscleSetsThisWeek,
  setsLoggedInRange,
  volumeInRange,
  weeklyVolumeSummary,
  workoutsCompletedInRange,
} from './muscleStats';
import { getWeekStart } from './dateUtils';

export default function MuscleDistribution({ workoutLog, language, weightUnit, t, onShare, onPickExercise, onOpenExercise }) {
  const [period, setPeriod] = useState(7);
  const counts = muscleSetsInRange(workoutLog, period);
  const weekCounts = muscleSetsThisWeek(workoutLog);
  const bars = muscleBarData(counts, language).map((row) => ({
    ...row,
    label: getMuscleLabel(row.key, language),
  }));
  const week = weeklyVolumeSummary(workoutLog);
  const thisStart = getWeekStart(new Date());
  const weekVolume = volumeInRange(workoutLog, thisStart, new Date());
  const workouts = workoutsCompletedInRange(workoutLog, period);
  const sets = setsLoggedInRange(workoutLog, period);
  const volumeNow = volumeInRange(workoutLog, (() => {
    const d = new Date();
    d.setDate(d.getDate() - (period - 1));
    d.setHours(0, 0, 0, 0);
    return d;
  })(), new Date());

  const changeLabel = week.changePct == null
    ? t.noLastWeek
    : `${week.changePct >= 0 ? '+' : ''}${week.changePct.toFixed(1)}% ${t.vsLastWeek}`;

  return (
    <div className="muscle-dist">
      <div className="weekly-volume-card">
        <div className="weekly-volume-head">
          <div>
            <p className="weekly-volume-label">{t.weeklyVolume}</p>
            <p className="weekly-volume-value">
              {Math.round(weekVolume).toLocaleString()} {weightUnit}
            </p>
            <p className={`weekly-volume-change${week.changePct != null && week.changePct >= 0 ? ' is-up' : ''}`}>
              {changeLabel}
            </p>
          </div>
          <button
            type="button"
            className="btn btn-dark"
            onClick={() => onShare({
              volumeLabel: `${Math.round(weekVolume).toLocaleString()} ${weightUnit}`,
              changeLabel,
              bars,
            })}
          >
            <IconShare size={16} /> {t.share}
          </button>
        </div>
        <div className="weekly-volume-meta">
          <span>{workouts} {t.workouts}</span>
          <span>{sets} {t.sets}</span>
          <span>{Math.round(volumeNow).toLocaleString()} {weightUnit} · {period === 7 ? t.last7 : t.last30}</span>
        </div>
      </div>

      <div className="period-row">
        <button type="button" className={`btn btn-dark period-btn${period === 7 ? ' is-active' : ''}`} onClick={() => setPeriod(7)}>{t.last7}</button>
        <button type="button" className={`btn btn-dark period-btn${period === 30 ? ' is-active' : ''}`} onClick={() => setPeriod(30)}>{t.last30}</button>
      </div>

      <p className="muscle-dist-title">{t.muscleDistribution}</p>
      <BodyMap language={language} heat={counts} weekHeat={weekCounts} t={t} onPickExercise={onPickExercise} onOpenExercise={onOpenExercise} />

      {bars.length > 0 ? (
        <div className="muscle-bar-wrap">
          <p className="muscle-dist-sub">{t.setsPerMuscle}</p>
          <ResponsiveContainer width="100%" height={Math.max(180, bars.length * 36)}>
            <BarChart data={bars} layout="vertical" margin={{ left: 8, right: 12, top: 8, bottom: 8 }}>
              <XAxis type="number" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
              <YAxis type="category" dataKey="label" width={88} tick={{ fill: 'var(--text-muted)', fontSize: 12 }} />
              <Tooltip
                contentStyle={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '8px' }}
                formatter={(value) => [`${value} ${language === 'da' ? 'sæt' : 'sets'}`, '']}
              />
              <Bar dataKey="value" fill="var(--accent)" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="muscle-empty">{t.muscleEmpty}</p>
      )}
    </div>
  );
}
